import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CustomModal } from "../CustomModal";
import { IServiceVideoChat, IServiceVideoChatDevice } from "@obsbot/remo-services";
import { useTranslate } from "@obsbot/remo-i18n";
import { Checkbox, Select } from "antd";
import { useSafePromise } from "../../utils/hooks";
import DropdownSVG from "../icons/Dropdown.svg";
import PreviewCameraSVG from "@obsbot/remo-pages/src/UserSettingPage/RTCSettingPage/components/icons/PreviewCam.svg";
import { useGlobalStore, usePreferencesStore } from "@obsbot/remo-stores";
import "./index.less";
import { I420Canvas3DRenderer } from "@obsbot/remo-utils";

export interface PreviewCameraProps {
    visible: boolean;
    isPreview?: boolean;
    setVisible: (flag: boolean) => void;
    rtc: IServiceVideoChat;
    confirmClick: () => void;
    onClickClose?: () => void;
}

export const PreviewCameraModal: React.FC<PreviewCameraProps> = ({
    visible,
    isPreview = true,
    setVisible,
    rtc,
    confirmClick,
    onClickClose,
}): ReactElement => {
    const t = useTranslate();
    // 预览画面的容器
    const previewContainer = useRef<HTMLDivElement>(null);
    // 相机设备列表
    const [camDeviceList, setCamDeviceList] = useState<IServiceVideoChatDevice[]>([]);
    // 当前设备ID
    const [currentDeviceID, setCurrentDeviceID] = useState<string | null>(null);
    const globalStore = useGlobalStore();
    const preferenceStore = usePreferencesStore(globalStore.userInfo?.user_id ?? "")?.(
        state => state,
    );
    // 是否总是预览摄像头
    const isAlwaysPreview = useMemo(() => {
        return preferenceStore?.alwaysPreviewCam;
    }, [preferenceStore]);
    const sp = useSafePromise();
    useEffect(() => {
        rtc.setVideoMirror(preferenceStore?.videoMirror ?? true);
    }, [rtc, preferenceStore?.videoMirror]);
    // 处理点击外面
    const innerRef = useRef<HTMLDivElement | null>(null);
    const [isAlert, setIsAlert] = useState(false);
    useEffect(() => {
        const handleFn = (e: MouseEvent): void => {
            if (isPreview) {
                return;
            }
            const findEl = (el: Element): Element | null => {
                if (el.classList.contains("PreviewCameraModal")) {
                    return el;
                } else if (el.parentElement) {
                    return findEl(el.parentElement);
                } else {
                    return null;
                }
            };
            const allowClickEl = findEl(e.target as Element);
            if (!allowClickEl) {
                const modalContainer =
                    innerRef.current?.parentNode?.parentNode?.parentNode?.parentNode;
                if (modalContainer) {
                    const handleAnimationend = (): void => {
                        modalContainer.removeEventListener("animationend", handleAnimationend);
                        setIsAlert(false);
                    };
                    modalContainer.addEventListener("animationend", handleAnimationend);
                }
                setIsAlert(true);
            }
        };
        if (visible) {
            document.addEventListener("click", handleFn);
        } else {
            document.removeEventListener("click", handleFn);
        }
    }, [visible]);
    useEffect(() => {
        const updateCameraDevices = async (deviceID?: string): Promise<void> => {
            const devices = await sp(rtc?.getCameraDevices());
            rtc && setCamDeviceList(devices);
            const camId = deviceID || rtc.getCameraID() || devices[0]?.deviceId;
            if (devices.length === 0 || devices.every(device => device.deviceId !== camId)) {
                return;
            }
            rtc && setCurrentDeviceID(camId || null);
            if (camId) {
                onChooseCam(camId);
            }
        };
        updateCameraDevices();
        rtc?.events.on("camera-changed", updateCameraDevices);
        return () => {
            rtc?.events.off("camera-changed", updateCameraDevices);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rtc, sp]);
    // 当选择了camera设备
    const onChooseCam = useCallback(
        (deviceID: string): void => {
            setCurrentDeviceID(deviceID);
            rtc?.setCameraID(deviceID);
        },
        [rtc],
    );
    // 处理切换是否总是预览摄像头
    const handleAlwaysPreview = (): void => {
        preferenceStore?.updateAlwaysPreviewCam(!isAlwaysPreview);
    };
    // 处理打开摄像头
    useEffect(() => {
        if (rtc?.getCameraID() && currentDeviceID) {
            rtc.setVideoMirror(preferenceStore?.videoMirror ?? true);
            rtc?.startCameraTest(previewContainer.current!);
        }
        return () => rtc?.stopCameraTest();
    }, [rtc, preferenceStore?.videoMirror, currentDeviceID]);
    // 处理中途插入摄像头
    useEffect(() => {
        if (currentDeviceID && rtc?.getCameraID()) {
            rtc?.stopCameraTest();
            rtc?.startCameraTest(previewContainer.current!);
        }
    }, [rtc, currentDeviceID]);
    // 处理获取预览摄像头视频流数据
    useEffect(() => {
        if (!rtc) {
            return;
        }
        if (!currentDeviceID) {
            return;
        }
        const canvasContainer = previewContainer.current as HTMLDivElement;
        const canvas = canvasContainer?.children[0]?.children[0] as HTMLCanvasElement;
        let renderer: I420Canvas3DRenderer;
        if (canvas && canvasContainer && canvas.tagName === "CANVAS") {
            renderer = new I420Canvas3DRenderer(canvas);
        }
        const handlePreviewVideoData = ({
            data,
            width,
            height,
        }: {
            data: Uint8Array;
            width: number;
            height: number;
        }): void => {
            if (canvas && canvasContainer && canvas.tagName === "CANVAS") {
                renderer.draw(data, width, height, preferenceStore?.videoMirror ? 360 : 0);
            } else {
                if (canvasContainer) {
                    canvasContainer.innerHTML = "";
                }
            }
        };
        rtc.events.on("preview-video-stream-data", handlePreviewVideoData);
        return () => {
            rtc.events.off("preview-video-stream-data", handlePreviewVideoData);
        };
    }, [rtc, preferenceStore?.videoMirror, currentDeviceID]);
    return (
        <CustomModal
            className={
                "PreviewCameraModal" +
                (isPreview ? "" : " preview-modal-join") +
                (isAlert ? " preview-modal-alert" : "")
            }
            closable={isPreview}
            destroyOnHidden={true}
            footer={null}
            open={visible}
            renderHeader={
                isPreview ? (
                    t("previewCam.title")
                ) : (
                    <div>
                        <span>{t("channel-ready")}</span>
                        <span className="preview-modal-ready-description">
                            {t("channel-ready-description")}
                        </span>
                    </div>
                )
            }
            setVisible={setVisible}
            width={380}
            onClickClose={() => onClickClose?.()}
        >
            <div ref={innerRef} className="preview-modal-preview">
                <div className="modal-form-item-title">
                    <span>{t("previewCam.previewScreen")}</span>
                </div>
                <div
                    ref={previewContainer}
                    className={
                        "preview-modal-preview-content" +
                        (preferenceStore?.videoMirror ?? true ? " mirror" : "")
                    }
                >
                    <img src={PreviewCameraSVG} />
                </div>
            </div>
            <div className="preview-modal-cam-list">
                <div className="modal-form-item-title">
                    <span>{t("previewCam.camDevice")}</span>
                </div>
                <div className="preview-modal-cam-selector">
                    <Select
                        key={currentDeviceID}
                        defaultValue={currentDeviceID}
                        options={camDeviceList.map(v => ({
                            label: v.label,
                            value: v.deviceId,
                            title: "",
                        }))}
                        rootClassName="Setting-Selector"
                        style={{ width: "100%" }}
                        suffixIcon={
                            <div className="dropdownIcon">
                                <img src={DropdownSVG} />
                            </div>
                        }
                        onChange={value => onChooseCam(value)}
                    />
                </div>
            </div>
            <div className="preview-modal-cam-input">
                <Checkbox checked={isAlwaysPreview} onChange={handleAlwaysPreview}>
                    <span>{t("settings.rtc-setting.alwaysPreview")}</span>
                </Checkbox>
                <div
                    className="preview-modal-cam-open-cam-btn"
                    onClick={() => {
                        currentDeviceID && onChooseCam(currentDeviceID);
                        confirmClick();
                    }}
                >
                    {isPreview ? t("confirm") : t("take-me-to-channel")}
                </div>
            </div>
        </CustomModal>
    );
};
